import { memo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Clock, 
  TrendingDown, 
  Shield, 
  Star, 
  Activity,
  HeartPulse
} from "lucide-react";
import type { Treatment } from "@/types/schema";

interface TreatmentOverviewProps {
  treatment: Treatment;
  doctorCount: number;
  hospitalCount: number;
}

export const TreatmentOverview = memo(function TreatmentOverview({ 
  treatment,
  doctorCount,
  hospitalCount
}: TreatmentOverviewProps) {
  const highlights = [
    {
      icon: TrendingDown,
      label: "Savings",
      value: `Up to ${treatment.savings_percentage}%`,
      description: "vs USA/UK prices",
      color: "text-accent",
    },
    {
      icon: Clock,
      label: "Duration",
      value: `${treatment.duration_days} days`,
      description: "typical stay",
      color: "text-primary",
    },
    {
      icon: Shield,
      label: "Hospitals",
      value: hospitalCount > 0 ? hospitalCount.toString() : "Certified",
      description: "Partner hospitals",
      color: "text-accent",
    },
    {
      icon: Star,
      label: "Specialists",
      value: doctorCount > 0 ? doctorCount.toString() : "On Demand",
      description: doctorCount > 0 ? "verified doctors" : "Top Specialists",
      color: "text-primary",
    },
  ];

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <HeartPulse className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle as="h2" className="text-xl">Treatment Overview</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Key information at a glance
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Description */}
        {(treatment.long_description || treatment.description) && (
          <p className="text-muted-foreground mb-6 leading-relaxed">
            {treatment.long_description || treatment.description}
          </p>
        )}

        {/* Procedure Details */}
        {treatment.procedure_details && (
          <div className="mb-6">
            <h3 className="font-medium text-sm text-secondary mb-2 flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary" />
              Procedure Details
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed pl-6">
              {treatment.procedure_details}
            </p>
          </div>
        )}

        {/* Recovery Info */}
        {treatment.recovery_info && (
          <div className="mb-6">
            <h3 className="font-medium text-sm text-secondary mb-2 flex items-center gap-2">
              <HeartPulse className="w-4 h-4 text-accent" />
              Recovery Information
            </h3>
            <p className="text-sm text-muted-foreground leading-relaxed pl-6">
              {treatment.recovery_info}
            </p>
          </div>
        )}
        


        {/* Highlights Grid */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-4 border-t border-border/50">
          {highlights.map((item) => (
            <div 
              key={item.label}
              className="text-center p-2 sm:p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <item.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${item.color} mx-auto mb-1 sm:mb-2`} />
              <div className="font-semibold text-base sm:text-lg text-secondary truncate">{item.value}</div>
              <div className="text-[10px] sm:text-xs text-muted-foreground truncate">{item.description}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
});
